import React, { useCallback, useEffect, useRef, useState } from "react";
import { CarouselDots } from "./CarouselDots";

type CarouselBreakpoints = {
    /** Cards visible at >= 1024px (default: 3) */
    lg?: number;
    /** Cards visible at >= 768px (default: 2) */
    md?: number;
    /** Cards visible below 768px (default: 1) */
    sm?: number;
};

type CarouselProps<T> = {
    items: T[];
    renderItem: (item: T, index: number) => React.ReactNode;
    /** Auto-advance interval in ms. 0 = disabled. Default: 4000 */
    autoAdvanceMs?: number;
    /** Responsive cards-per-view overrides */
    breakpoints?: CarouselBreakpoints;
    /** Additional className on the outer wrapper */
    className?: string;
    /** aria-label for the carousel region */
    ariaLabel?: string;
    /** Pause auto-advance when an overlay is open */
    paused?: boolean;
};

const DEFAULT_BREAKPOINTS: Required<CarouselBreakpoints> = { lg: 3, md: 2, sm: 1 };

export const Carousel = <T,>({
    items,
    renderItem,
    autoAdvanceMs = 4000,
    breakpoints,
    className = "",
    ariaLabel = "Carousel",
    paused: externalPaused = false,
}: CarouselProps<T>) => {
    const bp = { ...DEFAULT_BREAKPOINTS, ...breakpoints };
    const count = items.length;

    // Start at middle copy for infinite scroll
    const [currentIndex, setCurrentIndex] = useState(count);
    const [isTransitioning, setIsTransitioning] = useState(true);
    const [isHovered, setIsHovered] = useState(false);
    const [cardsPerView, setCardsPerView] = useState(bp.lg);

    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const touchStartRef = useRef(0);
    const touchDeltaRef = useRef(0);

    const isPaused = isHovered || externalPaused;

    // Triple items for infinite scroll
    const infiniteItems = count > 0 ? [...items, ...items, ...items] : [];

    // Responsive cards-per-view
    useEffect(() => {
        const update = () => {
            const w = window.innerWidth;
            if (w >= 1024) {
                setCardsPerView(bp.lg);
            } else if (w >= 768) {
                setCardsPerView(bp.md);
            } else {
                setCardsPerView(bp.sm);
            }
        };
        update();
        window.addEventListener("resize", update);
        return () => window.removeEventListener("resize", update);
    }, [bp.lg, bp.md, bp.sm]);

    // Infinite loop boundary reset
    useEffect(() => {
        if (count === 0) {
            return;
        }
        const upper = count * 2;
        const lower = count;

        if (currentIndex >= upper || currentIndex < lower) {
            resetTimeoutRef.current = setTimeout(() => {
                setIsTransitioning(false);
                const normalized = ((currentIndex % count) + count) % count;
                setCurrentIndex(count + normalized);
                setTimeout(() => setIsTransitioning(true), 50);
            }, 500);
        }

        return () => {
            if (resetTimeoutRef.current) {
                clearTimeout(resetTimeoutRef.current);
            }
        };
    }, [currentIndex, count]);

    // Auto-advance
    useEffect(() => {
        if (isPaused || count === 0 || autoAdvanceMs <= 0) {
            return;
        }
        intervalRef.current = setInterval(() => {
            setCurrentIndex(prev => prev + 1);
        }, autoAdvanceMs);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isPaused, count, autoAdvanceMs]);

    const resetInterval = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
        if (!isPaused && count > 0 && autoAdvanceMs > 0) {
            intervalRef.current = setInterval(() => {
                setCurrentIndex(prev => prev + 1);
            }, autoAdvanceMs);
        }
    }, [isPaused, count, autoAdvanceMs]);

    const next = useCallback(() => {
        setCurrentIndex(prev => prev + 1);
        resetInterval();
    }, [resetInterval]);

    const prev = useCallback(() => {
        setCurrentIndex(prev => prev - 1);
        resetInterval();
    }, [resetInterval]);

    const goToDot = useCallback(
        (dotIndex: number) => {
            setCurrentIndex(count + dotIndex);
            resetInterval();
        },
        [count, resetInterval]
    );

    const activeDot = ((currentIndex % count) + count) % count;

    // Touch/swipe
    const handleTouchStart = (e: React.TouchEvent) => {
        touchStartRef.current = e.touches[0].clientX;
        touchDeltaRef.current = 0;
    };
    const handleTouchMove = (e: React.TouchEvent) => {
        touchDeltaRef.current = e.touches[0].clientX - touchStartRef.current;
    };
    const handleTouchEnd = () => {
        if (touchDeltaRef.current < -50) {
            next();
        } else if (touchDeltaRef.current > 50) {
            prev();
        }
        touchDeltaRef.current = 0;
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "ArrowLeft") {
            prev();
        } else if (e.key === "ArrowRight") {
            next();
        }
    };

    if (count === 0) {
        return null;
    }

    const cardWidthPercent = 100 / cardsPerView;
    const translateX = currentIndex * cardWidthPercent;

    return (
        <div
            className={`relative group ${className}`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            role="region"
            aria-label={ariaLabel}
        >
            <div className="overflow-hidden">
                <div
                    className="flex"
                    style={{
                        transform: `translateX(-${translateX}%)`,
                        transition: isTransitioning ? "transform 500ms ease-in-out" : "none",
                    }}
                >
                    {/*
                        Nur die MITTLERE Kopie ist benannt (12.8.2026).

                        Das Karussell legt die Liste dreifach hintereinander, damit
                        es endlos laufen kann. Alle drei Kopien standen im
                        Dokument — ein Vorlese-Programm las jeden Beitrag DREIMAL.
                        Am gebauten Artefakt nachgezaehlt: 15 Portfolio-Karten
                        statt 5, 18 Blog-Ueberschriften statt 6.

                        Dieselbe Korrektur wie beim Logoband in `TrustSection`:
                        Dort tragen die Kopien 2–4 ein leeres `alt` und
                        `aria-hidden`, sonst laese ein Screenreader jeden
                        Kundennamen viermal vor.

                        ⚠️ `aria-hidden` allein waere ein NEUER Fehler: Die Karten
                        sind Links. Ein fokussierbares Element in einem
                        `aria-hidden`-Teilbaum ist die axe-Regel
                        `aria-hidden-focus` — der Besucher tabbte in einen Link,
                        den die Vorlese-Stimme nicht nennt. `inert` nimmt den
                        Teilbaum zusaetzlich aus der Tab-Reihenfolge.

                        React 18 kennt `inert` nicht als booleanes Attribut, es
                        wuerde als `inert="false"` im HTML landen und damit GELTEN.
                        Deshalb der leere String, und nur wenn gesetzt.
                    */}
                    {infiniteItems.map((item, idx) => {
                        const istKopie = idx < count || idx >= count * 2;
                        return (
                            <div
                                key={idx}
                                className="flex-shrink-0 px-3"
                                style={{ width: `${cardWidthPercent}%` }}
                                aria-hidden={istKopie || undefined}
                                {...(istKopie ? { inert: "" } : {})}
                            >
                                {renderItem(item, idx % count)}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Navigation arrows (visible on hover) */}
            <button
                onClick={prev}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-primary-x-light hover:shadow-xl hover:scale-105 transition-all duration-300 z-10 cursor-pointer opacity-0 group-hover:opacity-100"
                aria-label="Previous"
            >
                <svg className="w-5 h-5 text-primary-light" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
            </button>
            <button
                onClick={next}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-primary-x-light hover:shadow-xl hover:scale-105 transition-all duration-300 z-10 cursor-pointer opacity-0 group-hover:opacity-100"
                aria-label="Next"
            >
                <svg className="w-5 h-5 text-primary-light" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
            </button>

            {/* Dot navigation */}
            {count > 1 && (
                <CarouselDots totalSlides={count} currentSlide={activeDot} onSlideChange={goToDot} className="mt-8" />
            )}
        </div>
    );
};
